import { Web3DPlugin } from '@technology/web3d';
import type { PointIcon } from '@technology/web3d-plugin-icons';
import {
  ThreejsPlugin,
  type MeshPoints,
} from '@technology/web3d-plugin-threejs';
import type { Api } from '@technology/web3d/dist/esnext/Api.js';
import {
  createIssueMarkerImageLoader,
  createIssueMarkerSprites,
  disposeIssueMarkerSprites,
} from './issue-marker-sprite-resources';
import { ISSUE_MARKERS_MODEL_ID } from './issue-marker-sprite.types';
import { IssueMarkersModel } from './issue-markers-model';

/**
 * Owns this viewer's model and every issue marker sprite in it. Operations are
 * chained so an unload never overlaps an add.
 */
export class IssueMarkersPlugin extends Web3DPlugin {
  private viewerApi: Api | undefined;
  private model: IssueMarkersModel | undefined;
  private operationChain: Promise<void> = Promise.resolve();
  private readonly ownedSprites = new WeakMap<object, readonly MeshPoints[]>();
  private readonly latestRequests = new WeakMap<object, number>();
  private readonly loadIconImage = createIssueMarkerImageLoader();
  private requestCount = 0;

  public get name(): string {
    return ISSUE_MARKERS_MODEL_ID;
  }

  public set api(value: Api) {
    this.viewerApi = value;
  }

  public getModel(): IssueMarkersModel | undefined {
    return this.model;
  }

  public hasIcons(owner: object): boolean {
    return (this.ownedSprites.get(owner)?.length ?? 0) > 0;
  }

  /**
   * Replaces the markers `owner` set earlier with `icons`. A later call for the
   * same owner supersedes this one, so its sprites never reach the model.
   */
  public setIcons(owner: object, icons: readonly PointIcon[]): Promise<void> {
    const request = ++this.requestCount;
    this.latestRequests.set(owner, request);
    const isSuperseded = (): boolean =>
      this.latestRequests.get(owner) !== request;

    const operation = this.operationChain.then(async () => {
      if (isSuperseded()) {
        return;
      }

      const api = this.requireApi();
      const next = await createIssueMarkerSprites(
        api.plugins.get(ThreejsPlugin),
        icons,
        this.loadIconImage
      );
      if (isSuperseded()) {
        disposeIssueMarkerSprites(next);
        return;
      }

      await this.applySprites(api, owner, next);
    });
    // Not logged here: the caller receives this rejection and logs it.
    this.operationChain = operation.catch(() => undefined);
    return operation;
  }

  public redraw(): void {
    this.viewerApi?.renderingManager.redraw();
  }

  private requireApi(): Api {
    if (!this.viewerApi) {
      throw new Error('IssueMarkersPlugin: api is not set');
    }
    return this.viewerApi;
  }

  private async applySprites(
    api: Api,
    owner: object,
    next: readonly MeshPoints[]
  ): Promise<void> {
    const previous = this.ownedSprites.get(owner) ?? [];
    const current = this.model;
    const hasEmptyModel = current?.root.children.length === 0;
    // An empty model left by a failed unload is retried on the next empty update.
    if (previous.length === 0 && next.length === 0 && !hasEmptyModel) {
      return;
    }

    if (previous.length > 0) {
      current?.remove(...previous);
      disposeIssueMarkerSprites(previous);
      this.ownedSprites.delete(owner);
    }

    if (next.length > 0) {
      try {
        (current ?? this.createModel(api)).add(...next);
      } catch (error: unknown) {
        disposeIssueMarkerSprites(next);
        throw error;
      }
      this.ownedSprites.set(owner, next);
    } else if (current && current.root.children.length === 0) {
      await api.models.remove(current.modelId);
      this.model = undefined;
    }

    this.redraw();
  }

  private createModel(api: Api): IssueMarkersModel {
    const model = new IssueMarkersModel();
    api.models.add(model);
    this.model = model;
    return model;
  }
}
